import { useTranslation } from "react-i18next";
import {
  useUpdateWorkstationPreferences,
  useWorkstationPreferences,
} from "../../hooks/indigo/useWorkstationApi";
import type { CompanionMode } from "../../types/indigo";

/**
 * CompanionModeToggle — Reader / Companion picker.
 *
 * - Reader: numbers without interpretation (pattern readings hidden,
 *   except safety overrides which always surface).
 * - Companion: pattern-matched interpretation with peer-reviewed
 *   attribution attached.
 * - Auto: Solo athletes default to Companion; Coached athletes default
 *   to Reader so the coach owns the interpretation.
 */
export function CompanionModeToggle() {
  const prefs = useWorkstationPreferences();
  const update = useUpdateWorkstationPreferences();
  const { t } = useTranslation("common");
  const mode: CompanionMode = prefs.data?.companion_mode ?? "auto";

  const options: { value: CompanionMode; label: string; desc: string }[] = [
    {
      value: "reader",
      label: "Reader",
      desc: t("companion.reader_desc", { defaultValue: "Numbers only, no interpretation" }),
    },
    {
      value: "companion",
      label: "Companion",
      desc: t("companion.companion_desc", {
        defaultValue: "Pattern interpretation with peer-reviewed attribution",
      }),
    },
    {
      value: "auto",
      label: "Auto",
      desc: t("companion.auto_desc", { defaultValue: "Solo → Companion · Coached → Reader" }),
    },
  ];

  return (
    <div className="ws-companion-toggle">
      <div className="ws-companion-toggle__head">
        <h3 className="ws-companion-toggle__title">
          {t("companion.title", { defaultValue: "Companion mode" })}
        </h3>
        <p className="ws-companion-toggle__sub">
          {t("companion.sub", {
            defaultValue: "Storica can show data only, or surface pattern interpretation.",
          })}
        </p>
      </div>
      <div role="radiogroup" className="ws-companion-toggle__group">
        {options.map((opt) => {
          const active = mode === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={update.isPending}
              className={`ws-companion-toggle__opt${active ? " is-active" : ""}`}
              onClick={() => {
                if (!active) update.mutate({ companion_mode: opt.value });
              }}
            >
              <span className="ws-companion-toggle__opt-name">{opt.label}</span>
              <span className="ws-companion-toggle__opt-desc">{opt.desc}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
